// Documents — write-through layer to the bvndle-notes Worker (Phase 2).
//
// The KnowledgePage drops parsed docs in via `uploadDocument(doc)`. We:
//   1. POST the parsed JSON to /docs. The Worker chunks + embeds + writes
//      to Vectorize and KV in one round-trip.
//   2. If the doc has binary bytes (image data URL, optionally PDF source),
//      ship them separately to /docs/:id/blob so the Worker can store them
//      in R2 — KV values are capped at 25 MB and base64 image payloads
//      easily blow past that.
//
// `fetchDocuments()` returns the team-wide list (metadata only). The full
// doc + sections come via `fetchDocument(id)`.
//
// Errors propagate as thrown `Error`s with descriptive messages so the
// KnowledgePage's parse-error surface can show them in the live error log.

import { NOTES_ENDPOINT } from "../config/endpoints";
import type { ParsedDocument } from "./document-parsers";

/** What the Worker returns from `GET /docs` — metadata only, no full text. */
export interface DocSummary {
  id: string;
  fileName: string;
  kind: ParsedDocument["kind"];
  charCount: number;
  pulledAt: string;
  bytes: number;
  image?: {
    width: number;
    height: number;
    mimeType: string;
  };
  uploadedBy?: string;
  hasBlob?: boolean;
  chunkCount?: number;
}

/**
 * Push a parsed doc to the Worker. Returns the synced doc record. If the
 * doc carries an image dataUrl, the binary bytes are uploaded separately
 * to R2 before this resolves.
 */
export async function uploadDocument(
  doc: ParsedDocument,
  uploadedBy?: string,
): Promise<DocSummary> {
  // We strip the dataUrl out of the payload going to KV — it's huge and
  // the binary lives in R2 instead. The Worker does the same scrubbing
  // defensively, but doing it client-side saves bandwidth.
  const { image, ...rest } = doc;
  const payload = {
    ...rest,
    uploadedBy,
    hasBlob: Boolean(image?.dataUrl),
    image: image
      ? { width: image.width, height: image.height, mimeType: image.mimeType }
      : undefined,
  };

  const res = await fetch(`${NOTES_ENDPOINT}/docs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const detail = await safeReadError(res);
    throw new Error(
      `Worker rejected /docs upload (${res.status}): ${detail}`,
    );
  }
  const body = (await res.json()) as {
    doc: DocSummary;
    warning?: string;
  };
  if (body.warning) {
    console.warn("[documents-sync] worker warning:", body.warning);
  }

  // Now ship the blob if there is one. We send the raw bytes (not the data
  // URL) so R2 doesn't double-store the base64 wrapper.
  if (image?.dataUrl) {
    const blob = dataUrlToBlob(image.dataUrl);
    await uploadBlob(doc.id, blob);
  }
  return body.doc;
}

/** Fetch the team-wide document list (metadata only). */
export async function fetchDocuments(): Promise<DocSummary[]> {
  const res = await fetch(`${NOTES_ENDPOINT}/docs`);
  if (!res.ok) {
    throw new Error(`Worker list /docs failed (${res.status})`);
  }
  const body = (await res.json()) as { docs: DocSummary[] };
  return body.docs ?? [];
}

/** Fetch one doc with full sections + text. Returns null if not found. */
export async function fetchDocument(id: string): Promise<ParsedDocument | null> {
  const res = await fetch(`${NOTES_ENDPOINT}/docs/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Worker /docs/${id} failed (${res.status})`);
  }
  const body = (await res.json()) as { doc: ParsedDocument };
  return body.doc ?? null;
}

/** Delete a doc and its associated vectors + blob. */
export async function deleteDocument(id: string): Promise<void> {
  const res = await fetch(`${NOTES_ENDPOINT}/docs/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(`Worker delete /docs/${id} failed (${res.status})`);
  }
}

/** Fetch the binary blob (used to lazily reconstruct an image's data URL). */
export async function fetchBlobAsDataUrl(
  id: string,
  mimeType: string,
): Promise<string | null> {
  const res = await fetch(
    `${NOTES_ENDPOINT}/docs/${encodeURIComponent(id)}/blob`,
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Worker /docs/${id}/blob failed (${res.status})`);
  }
  const arrBuf = await res.arrayBuffer();
  // Convert ArrayBuffer → base64 → data URL. We do this in the main thread;
  // file sizes are bounded (image previews under ~10 MB).
  const bytes = new Uint8Array(arrBuf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  const base64 = btoa(binary);
  return `data:${mimeType};base64,${base64}`;
}

// ----- internals ----------------------------------------------------------

async function uploadBlob(id: string, blob: Blob): Promise<void> {
  const res = await fetch(
    `${NOTES_ENDPOINT}/docs/${encodeURIComponent(id)}/blob`,
    {
      method: "POST",
      headers: { "Content-Type": blob.type || "application/octet-stream" },
      body: blob,
    },
  );
  if (!res.ok) {
    const detail = await safeReadError(res);
    throw new Error(
      `Worker rejected blob upload for ${id} (${res.status}): ${detail}`,
    );
  }
}

function dataUrlToBlob(dataUrl: string): Blob {
  // "data:image/png;base64,iVBORw…" → Blob of the raw bytes.
  const commaIdx = dataUrl.indexOf(",");
  if (commaIdx < 0) {
    throw new Error("Malformed data URL — no comma separator");
  }
  const header = dataUrl.slice(0, commaIdx);
  const data = dataUrl.slice(commaIdx + 1);
  const mimeMatch = /data:([^;,]+)/.exec(header);
  const mime = mimeMatch?.[1] ?? "application/octet-stream";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

async function safeReadError(res: Response): Promise<string> {
  try {
    const body = await res.text();
    return body.slice(0, 300);
  } catch {
    return res.statusText;
  }
}
